/**
 * Session Manager - SOC2-Compliant Subdomain Isolation
 * 
 * Handles subdomain-specific session management to ensure
 * tenant isolation and SOC2 compliance.
 */

import { projectId } from './supabase/info';

import { bearerAuthHeader } from './apiHeaders';
// API Base URL — always use the canonical projectId from info.tsx to ensure
// session calls hit the same edge function as login (prevents cross-project
// JWT validation failures when getSupabaseConfig() resolves to a different project).
const getApiBase = () => {
  return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
};

/**
 * Get the root domain for the current environment
 * - Production: pathkit.pro
 * - Demo/Staging: staging.pathkit.pro
 */
export function getRootDomain(): string {
  const hostname = window.location.hostname;
  
  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost:5173';
  }
  
  // Handle Figma Preview
  if (hostname.includes('figmaiframepreview')) {
    return hostname;
  }
  
  // If we're already on a root domain, return it
  if (hostname === 'pathkit.pro' || hostname === 'www.pathkit.pro') {
    return 'pathkit.pro';
  }
  
  if (hostname === 'staging.pathkit.pro') {
    return 'staging.pathkit.pro';
  }
  
  // Check if we're on a demo/staging subdomain (contains "-demo" or starts with "demo-")
  if (hostname.includes('-demo.pathkit.pro') || hostname.startsWith('demo-')) {
    // For demo subdomains, return staging root
    return 'staging.pathkit.pro';
  }
  
  // Production subdomains (e.g., acme.pathkit.pro) -> root is pathkit.pro
  if (hostname.endsWith('.pathkit.pro')) {
    // If it doesn't contain 'staging' or 'demo', it's production
    if (!hostname.includes('staging') && !hostname.includes('demo')) {
      return 'pathkit.pro';
    }
    // Otherwise default to staging
    return 'staging.pathkit.pro';
  }
  
  // Default to pathkit.pro
  return 'pathkit.pro';
}

export interface SessionData {
  sessionId: string;
  userId: string;
  companyId: string;
  consultingFirmId: string;
  subdomain: string;
  role: string;
  userEmail: string;
  userName: string;
  expiresAt: number;
}

/**
 * Get the current subdomain from hostname
 */
export function getCurrentSubdomain(): string | null {
  const hostname = window.location.hostname;
  
  // Handle localhost
  if (hostname === 'localhost' || hostname.includes('localhost:')) {
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant');
  }
  
  // Handle root domains (both production and staging)
  if (hostname === 'pathkit.pro' || 
      hostname === 'www.pathkit.pro' ||
      hostname === 'staging.pathkit.pro' ||
      hostname.includes('figmaiframepreview')) {
    return null; // Root domain - no subdomain
  }
  
  // Extract subdomain (e.g., "premium-demo" from "premium-demo.pathkit.pro")
  const parts = hostname.split('.');
  if (parts.length >= 2 && parts[0] !== 'www') {
    return parts[0];
  }
  
  return null;
}

/**
 * Create a new session for a user
 */
export async function createSession(
  email: string, 
  password: string,
  subdomain: string | null
): Promise<{
  success: boolean;
  sessionId?: string;
  userId?: string;
  companyId?: string;
  consultingFirmId?: string;
  role?: string;
  userEmail?: string;
  userName?: string;
  user?: any;
  company?: any;
  expiresAt?: number;
  error?: string;
}> {
  try {
    console.log(`[SESSION] Creating session for ${email} on subdomain: ${subdomain || 'root'}`);
    
    const response = await fetch(`${getApiBase()}/session/create`, {
      method: 'POST',
      headers: {
        ...bearerAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        subdomain
      })
    });
    
    const data = await response.json();
    
    if (data.success && data.sessionId) {
      console.log(`[SESSION] Session created for ${data.userEmail}`);
      return data;
    } else {
      console.error('[SESSION] Login failed:', data.error);
      return { success: false, error: data.error || 'Login failed' };
    }
  } catch (error) {
    console.error('[SESSION] Error creating session:', error);
    return { success: false, error: 'Network error' };
  }
}

/**
 * Validate an existing session
 */
export async function validateSession(
  sessionId: string,
  subdomain: string | null
): Promise<{
  valid: boolean;
  session?: SessionData;
  user?: any;
  company?: any;
  error?: string;
}> {
  try {
    console.log(`[SESSION] Validating session on subdomain: ${subdomain || 'root'}`);
    
    const response = await fetch(`${getApiBase()}/session/validate`, {
      method: 'POST',
      headers: {
        ...bearerAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sessionId,
        subdomain
      })
    });
    
    const data = await response.json();
    
    if (data.valid) {
      console.log(`[SESSION] Session valid for ${data.session.userEmail} on ${subdomain}`);
    } else {
      console.log(`[SESSION] Session validation failed:`, data.error);
    }
    
    return data;
  } catch (error) {
    console.error('[SESSION] Error validating session:', error);
    return { valid: false, error: 'Validation error' };
  }
}

/**
 * Destroy a session (logout)
 * ENTERPRISE AUTH: Sends session token in Authorization Bearer header
 */
export async function destroySession(sessionToken: string): Promise<boolean> {
  try {
    console.log('[LOGOUT_TRACE] ========== DESTROYSESSION CALLED ==========');
    console.log('[LOGOUT_TRACE] destroySession function started');
    console.log('[LOGOUT_TRACE] sessionToken param:', sessionToken ? `${sessionToken.substring(0, 20)}...` : 'empty');
    console.log('[LOGOUT_TRACE] About to construct fetch URL...');
    
    const url = `${getApiBase()}/session/destroy`;
    console.log('[LOGOUT_TRACE] Fetch URL:', url);
    console.log('[LOGOUT_TRACE] About to create headers...');
    
    const headers = {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    };
    console.log('[LOGOUT_TRACE] Headers created (token redacted):', {
      ...headers,
      Authorization: `Bearer ${sessionToken.substring(0, 20)}...`
    });
    console.log('[LOGOUT_TRACE] About to call fetch...');
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });
    
    console.log('[LOGOUT_TRACE] fetch() returned, response status:', response.status);
    console.log('[LOGOUT_TRACE] response.ok:', response.ok);
    
    if (!response.ok) {
      console.error('[LOGOUT_TRACE] ❌ Failed to destroy session, status:', response.status);
      return false;
    }
    
    console.log('[LOGOUT_TRACE] ✅ Session destroyed successfully');
    return true;
  } catch (error) {
    console.error('[LOGOUT_TRACE] ❌ Error destroying session:', error);
    console.error('[LOGOUT_TRACE] Error stack:', error instanceof Error ? error.stack : 'no stack');
    return false;
  }
}

/**
 * Get session from localStorage - checks both canonical and legacy keys
 * 
 * ENTERPRISE AUTH: Now checks pathkit_session_token (canonical) first,
 * then falls back to legacy pathkit_session_id for backward compatibility
 */
export function getSessionFromLocalStorage(): {
  sessionId: string;
  user: any;
  company: any;
} {
  try {
    // PRIORITY 1: Check canonical token key (enterprise auth)
    const canonicalToken = localStorage.getItem('pathkit_session_token');
    if (canonicalToken) {
      console.log('[sessionManager] Found canonical session token (length:', canonicalToken.length, ')');
      
      // Also check for stored user/company data
      const userStr = localStorage.getItem('pathkit_user');
      const companyStr = localStorage.getItem('pathkit_company');
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const company = companyStr ? JSON.parse(companyStr) : null;
          
          return {
            sessionId: canonicalToken, // Use token as sessionId for backward compatibility
            user,
            company
          };
        } catch (e) {
          console.error('[sessionManager] Error parsing user/company data');
        }
      }
      
      // Token exists but no user data - return partial session
      return {
        sessionId: canonicalToken,
        user: null,
        company: null
      };
    }
    
    // FALLBACK: Check legacy session ID key
    const sessionId = localStorage.getItem('pathkit_session_id');
    if (sessionId) {
      console.log('[sessionManager] Found legacy session ID (migrating...)');
      
      const userStr = localStorage.getItem('pathkit_user');
      const companyStr = localStorage.getItem('pathkit_company');
      
      if (userStr) {
        const user = JSON.parse(userStr);
        const company = companyStr ? JSON.parse(companyStr) : null;
        
        // MIGRATION: Store in canonical key for next time
        localStorage.setItem('pathkit_session_token', sessionId);
        console.log('[sessionManager] Migrated legacy session to canonical key');
        
        return {
          sessionId,
          user,
          company
        };
      }
    }
    
    // No session found
    return {
      sessionId: '',
      user: null,
      company: null
    };
  } catch (error) {
    console.error('[sessionManager] Error reading session from localStorage:', error);
    return {
      sessionId: '',
      user: null,
      company: null
    };
  }
}

/**
 * Store session locally (subdomain-specific) - stores in BOTH canonical and legacy keys
 * 
 * ENTERPRISE AUTH: Stores in pathkit_session_token (canonical) and legacy keys
 */
export function storeSessionLocally(sessionId: string, user: any, company: any): void {
  try {
    // CANONICAL: Store in enterprise auth key
    localStorage.setItem('pathkit_session_token', sessionId);
    
    // LEGACY: Also store in old key for backward compatibility
    localStorage.setItem('pathkit_session_id', sessionId);
    
    // Store user and company data
    localStorage.setItem('pathkit_user', JSON.stringify(user));
    if (company) {
      localStorage.setItem('pathkit_company', JSON.stringify(company));
    }
    
    console.log(`[sessionManager] Session stored locally (canonical + legacy keys)`);
  } catch (error) {
    console.error('[sessionManager] Error storing session:', error);
  }
}

/**
 * Clear session from localStorage (subdomain-specific)
 */
export function clearSessionFromLocalStorage(): void {
  console.log('[sessionManager] Clearing session from localStorage...');
  
  const subdomain = getCurrentSubdomain();
  const prefix = subdomain ? `pathkit_${subdomain}_` : 'pathkit_';
  
  // CANONICAL KEY - MUST BE CLEARED
  localStorage.removeItem('pathkit_session_token');
  console.log('[sessionManager] ✅ Cleared canonical: pathkit_session_token');
  
  // LEGACY NON-PREFIXED KEYS
  localStorage.removeItem('pathkit_session_id');
  localStorage.removeItem('pathkit_access_token');
  localStorage.removeItem('pathkit_user');
  localStorage.removeItem('pathkit_company');
  console.log('[sessionManager] ✅ Cleared legacy non-prefixed keys');
  
  // SUBDOMAIN-PREFIXED KEYS (if applicable)
  localStorage.removeItem(`${prefix}session_id`);
  localStorage.removeItem(`${prefix}user`);
  localStorage.removeItem(`${prefix}company`);
  console.log('[sessionManager] ✅ Cleared subdomain-prefixed keys');
  
  console.log('[sessionManager] ✅ All session keys cleared');
}

/**
 * Check if user is on the correct subdomain
 * If not, and they have a valid session elsewhere, redirect them
 */
export async function checkSubdomainAccess(user: any, company: any): Promise<{ 
  correctSubdomain: boolean; 
  redirectTo?: string 
}> {
  const currentSubdomain = getCurrentSubdomain();
  
  // System admins can access any subdomain
  if (user.role === 'systemAdmin' || user.role === 'system_admin') {
    return { correctSubdomain: true };
  }
  
  // Allow Figma preview environments to bypass subdomain checks
  if (window.location.hostname.includes('figmaiframepreview')) {
    return { correctSubdomain: true };
  }
  
  // Determine which subdomain user should be on
  let expectedSubdomain: string | null = null;
  
  // Consultants and CF staff should be on their firm's subdomain
  if (user.consultingFirmId) {
    try {
      const response = await fetch(
        `${getApiBase()}/consulting-firms/${user.consultingFirmId}`,
        {
          headers: {
            ...bearerAuthHeader()
          }
        }
      );
      
      if (response.ok) {
        const firm = await response.json();
        expectedSubdomain = firm.subdomain;
      }
    } catch (error) {
      console.error('[SESSION] Error fetching firm subdomain:', error);
    }
  }
  
  // Clients should be on their company's subdomain (if assigned)
  if (company?.subdomain) {
    expectedSubdomain = company.subdomain;
  }
  
  // Check if user is on the correct subdomain
  if (expectedSubdomain && currentSubdomain !== expectedSubdomain) {
    const rootDomain = getRootDomain();
    return {
      correctSubdomain: false,
      redirectTo: `https://${expectedSubdomain}.${rootDomain}`
    };
  }
  
  return { correctSubdomain: true };
}